import { createContext, useContext, useEffect, useState } from "react";
import { listHotels } from "../api/hotels";
import { AI_REPORT_SETTINGS_CHANGED_EVENT } from "../api/aiReports";
import { useAuth } from "./AuthContext";

const AiReportAvailabilityContext = createContext(false);

export function AiReportAvailabilityProvider({ children }: { children: React.ReactNode }) {
  const { user, loading, availableCompanies } = useAuth();
  const companyIds = Array.from(new Set(
    availableCompanies.length
      ? availableCompanies.map((company) => company.id)
      : user?.company_id ? [user.company_id] : [],
  )).sort((a, b) => a - b);
  const scope = JSON.stringify([user?.id ?? null, companyIds]);
  const [availability, setAvailability] = useState({ scope: "", enabled: false });
  const [settingsRevision, setSettingsRevision] = useState(0);

  useEffect(() => {
    const refresh = () => setSettingsRevision((previous) => previous + 1);
    window.addEventListener(AI_REPORT_SETTINGS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(AI_REPORT_SETTINGS_CHANGED_EVENT, refresh);
  }, []);

  useEffect(() => {
    const [userId, ids] = JSON.parse(scope) as [number | null, number[]];
    if (loading || !userId || !ids.length) return;
    const controller = new AbortController();
    setAvailability({ scope, enabled: false });

    void (async () => {
      for (const companyId of ids) {
        try {
          const response = await listHotels(companyId, { ai_enable: true, page: 1, per_page: 1 }, controller.signal);
          if (controller.signal.aborted) return;
          if (response.total > 0) {
            setAvailability({ scope, enabled: true });
            return;
          }
        } catch {
          if (controller.signal.aborted) return;
        }
      }
    })();

    return () => controller.abort();
  }, [loading, scope, settingsRevision]);

  const enabled = !loading && availability.scope === scope && availability.enabled;
  return <AiReportAvailabilityContext.Provider value={enabled}>{children}</AiReportAvailabilityContext.Provider>;
}

export function useAiReportAvailability() {
  return useContext(AiReportAvailabilityContext);
}
